import React, { useMemo } from "react";
import { Edges, Html } from "@react-three/drei";
import * as THREE from "three";
import { dims, footprint, floorSpan, shade } from "./rigGeometry";
import { FOUNDATION_H, BASE_H, CAP_H, CORNER, PALETTE, claddingTextures, grimeTextures, metricBox, merge, box, cyl, WINDOW_SPEC, DOOR_W } from "./rigMaterials";
import { usePhotoMaps } from "./rigAssets";

export const ACCENT = "#266af3";
const SIDE_POS = (side, w, l) => {
  switch (side) {
    case "north": return { pos: [0, 0, -l / 2], rotY: Math.PI };
    case "east": return { pos: [w / 2, 0, 0], rotY: Math.PI / 2 };
    case "west": return { pos: [-w / 2, 0, 0], rotY: -Math.PI / 2 };
    default: return { pos: [0, 0, l / 2], rotY: 0 };
  }
};
const hashId = (id) => { let h = 0; for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0; return h; };
const FLAT_ROOF_VENT = new Set(["bolig", "sanitaer", "vaskeri", "garderobe", "kontor", "felles", "annet"]);

/* ---------------- geometry kit (cached per module type / floor height) ---------------- */

const partCache = new Map();

/** Rotate face-local items (x along the face, +z outwards, origin at the face centre) into module space. `dx` shifts along the face. */
function atFace(items, face, dx = 0) {
  const c = Math.cos(face.rotY), s = Math.sin(face.rotY);
  return items.map((it) => {
    const [x0, y, z] = it.p;
    const x = x0 + dx;
    const r = it.r || [0, 0, 0];
    return { ...it, p: [face.pos[0] + x * c + z * s, y, face.pos[2] - x * s + z * c], r: [r[0], r[1] + face.rotY, r[2]] };
  });
}

/** Framed window with sill – face-local coordinates (wall plane at z = 0, body surface 1 cm behind it). */
function windowItems(ww, wh, y, xOff) {
  const f = 0.06, d = 0.08, zf = 0.02;
  return {
    trims: [
      box(ww + 2 * f, f, d, xOff, y + wh / 2 + f / 2, zf), box(ww + 2 * f, f, d, xOff, y - wh / 2 - f / 2, zf),
      box(f, wh + 2 * f, d, xOff - ww / 2 - f / 2, y, zf), box(f, wh + 2 * f, d, xOff + ww / 2 + f / 2, y, zf),
      box(ww + 0.2, 0.035, 0.12, xOff, y - wh / 2 - f - 0.02, 0.03),
    ],
    glass: [box(ww, wh, 0.03, xOff, y, 0.005)],
    grime: [{ g: new THREE.PlaneGeometry(ww + 0.16, 0.8), p: [xOff, y - wh / 2 - f - 0.04 - 0.4, 0.014] }],
  };
}

/** Door frame, leaf(s), handle – door-local coordinates. */
function doorItems(dw, y0, double) {
  const items = [
    box(dw + 0.16, 0.08, 0.08, 0, y0 + 2.1 + 0.04, 0.03),
    box(0.08, 2.14, 0.08, -(dw / 2 + 0.04), y0 + 1.07, 0.03),
    box(0.08, 2.14, 0.08, dw / 2 + 0.04, y0 + 1.07, 0.03),
    box(dw, 2.06, 0.04, 0, y0 + 1.03, 0.005),
    box(0.03, 0.2, 0.05, double ? dw / 2 - 0.55 : dw / 2 - 0.13, y0 + 1.05, 0.04),
  ];
  if (double) items.push(box(0.03, 2.06, 0.05, 0, y0 + 1.03, 0.03), box(0.03, 0.2, 0.05, -(dw / 2 - 0.55), y0 + 1.05, 0.04));
  return items;
}

/** Galvanised entrance landing with one step and railings – door-local coordinates; ground is at y = −FOUNDATION_H. */
function entranceItems(dw) {
  const top = BASE_H; // landing flush with the module floor frame
  const W = Math.max(1.5, dw + 0.6);
  const hx = W / 2 - 0.03;
  const items = [
    box(W, 0.05, 1.0, 0, top - 0.025, 0.56),
    box(0.06, 0.14, 1.0, -hx, top - 0.1, 0.56),
    box(0.06, 0.14, 1.0, hx, top - 0.1, 0.56),
    box(W, 0.05, 0.32, 0, top - 0.195 - 0.025, 1.22),
    box(W, 0.195, 0.03, 0, top - 0.195 / 2, 1.075), // riser platform → step
    box(W, top - 0.195 + FOUNDATION_H, 0.03, 0, (top - 0.195 - FOUNDATION_H) / 2, 1.365), // riser step → ground
    cyl(0.025, top + FOUNDATION_H - 0.05, -hx + 0.05, (top - 0.05 - FOUNDATION_H) / 2, 1.0),
    cyl(0.025, top + FOUNDATION_H - 0.05, hx - 0.05, (top - 0.05 - FOUNDATION_H) / 2, 1.0),
  ];
  for (const sx of [-1, 1]) {
    const x = sx * hx;
    items.push(cyl(0.02, 1.05, x, top + 0.525, 0.12), cyl(0.02, 1.05, x, top + 0.525, 1.0), cyl(0.02, 0.9, x, top - 0.195 + 0.45, 1.34));
    items.push(cyl(0.018, 0.9, x, top + 1.05, 0.56, [Math.PI / 2, 0, 0]), cyl(0.018, 0.9, x, top + 0.55, 0.56, [Math.PI / 2, 0, 0]));
    items.push(cyl(0.018, 0.49, x, top + 0.88, 1.17, [(3 * Math.PI) / 4, 0, 0]));
  }
  return items;
}

/**
 * Build (or fetch) the geometry set for a module type. All parts share the same local frame; the returned object
 * maps part → BufferGeometry so the renderer can bind one material per part.
 */
function partsFor(type, w, h, l, stories, floorHeight) {
  const kind = type.kind || "annet";
  const windows = Math.max(0, Math.min(6, Number(type.windows) || 0));
  const doorSide = type.door_side || "south";
  const key = `${kind}|${w.toFixed(2)}|${h.toFixed(2)}|${l.toFixed(2)}|${windows}|${doorSide}|${stories}|${floorHeight}`;
  if (partCache.has(key)) return partCache.get(key);

  const wallH = h - BASE_H - CAP_H;
  const wallMid = BASE_H + wallH / 2;
  const trims = [], glass = [], frost = [], galv = [], frameItems = [], louvre = [], grime = [], splash = [];

  // body, floor frame, corner profiles, roof flashing ring + membrane
  const body = metricBox(w - 0.02, wallH, l - 0.02);
  body.translate(0, wallMid, 0);
  body.setAttribute("uv1", body.attributes.uv); // aoMap reads the second UV set
  // weathering: splash zone just above the floor frame on every face
  for (const side of ["north", "south", "east", "west"]) {
    const fc = SIDE_POS(side, w, l);
    const fw = (side === "north" || side === "south" ? w : l) - 2 * CORNER - 0.04;
    splash.push(...atFace([{ g: new THREE.PlaneGeometry(fw, 0.55), p: [0, BASE_H + 0.275, 0.014] }], fc));
  }
  const base = merge([box(w, BASE_H, l, 0, BASE_H / 2, 0)]);
  const cx = w / 2 - CORNER / 2, cz = l / 2 - CORNER / 2;
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) frameItems.push(box(CORNER, wallH, CORNER, sx * cx, wallMid, sz * cz));
  const capY = h - CAP_H / 2, ow = w + 0.04, ol = l + 0.04, t = 0.08;
  const cap = merge([
    box(ow, CAP_H, t, 0, capY, ol / 2 - t / 2), box(ow, CAP_H, t, 0, capY, -(ol / 2 - t / 2)),
    box(t, CAP_H, ol - 2 * t, ow / 2 - t / 2, capY, 0), box(t, CAP_H, ol - 2 * t, -(ow / 2 - t / 2), capY, 0),
  ]);
  const membrane = merge([box(ow - 2 * t, 0.03, ol - 2 * t, 0, h - 0.035, 0)]);

  // door – on a short (gable) face it sits off-centre so a window fits beside it, as on real accommodation modules
  const face = SIDE_POS(doorSide, w, l);
  const dw = DOOR_W[kind] || DOOR_W.default;
  const doorOnX = doorSide === "east" || doorSide === "west";
  const doorOnFace = (sx) => doorOnX && ((doorSide === "east") === (sx > 0));
  const spec = WINDOW_SPEC[kind];
  const gableWindow = !!spec && !doorOnX && w >= 2.4 && kind !== "lager";
  const doorDx = gableWindow ? -(w / 2 - dw / 2 - 0.19) : 0;
  trims.push(...atFace(doorItems(dw, BASE_H, kind === "lager"), face, doorDx));

  // windows – `windows` per long face, plus one per gable end (beside the door on the door face)
  if (spec) {
    const wh = Math.max(0.4, Math.min(spec.h, wallH - spec.sill - 0.25));
    const rows = [];
    for (let s = 0; s < stories; s++) {
      const y = s * floorHeight + spec.sill + wh / 2;
      if (y + wh / 2 <= h - CAP_H - 0.08) rows.push(y);
    }
    const target = spec.frosted ? frost : glass;
    const put = (fc, xOff, ww, dx = 0) => { for (const y of rows) { const it = windowItems(ww, wh, y, xOff); trims.push(...atFace(it.trims, fc, dx)); target.push(...atFace(it.glass, fc, dx)); grime.push(...atFace(it.grime, fc, dx)); } };
    if (windows > 0) {
      const ww = Math.max(0.6, Math.min(spec.w, l / windows - 0.6));
      for (const side of ["east", "west"]) {
        const fc = SIDE_POS(side, w, l);
        const sx = side === "east" ? 1 : -1;
        for (let i = 0; i < windows; i++) {
          const along = -l / 2 + ((i + 0.5) * l) / windows; // position along the face
          if (doorOnFace(sx) && Math.abs(along) < dw / 2 + 0.75) continue;
          put(fc, -sx * along, ww); // face-local x runs opposite to z on the east face
        }
      }
    }
    if (!doorOnX && w >= 2.4) {
      for (const side of ["south", "north"]) {
        const fc = SIDE_POS(side, w, l);
        if (side === doorSide) {
          if (!gableWindow) continue;
          const left = doorDx + dw / 2 + 0.22, right = w / 2 - CORNER - 0.08;
          const gw = Math.min(spec.w, 0.9, right - left);
          if (gw >= 0.45) put(fc, (left + right) / 2, gw);
        } else put(fc, 0, Math.min(spec.w, w - 1.2));
      }
    }
  }

  // corridor: continuous glazing band with mullions on both long faces
  if (kind === "gang") {
    const gl = l - 2 * CORNER - 0.1, y0 = 1.0, y1 = 2.15, yc = (y0 + y1) / 2, gh = y1 - y0;
    const n = Math.max(1, Math.round(gl / 1.2));
    const seg = gl / n;
    for (const sx of [-1, 1]) {
      const x = sx * (w / 2 + 0.02);
      trims.push(box(0.08, 0.06, gl + 0.08, x, y1 + 0.03, 0), box(0.08, 0.06, gl + 0.08, x, y0 - 0.03, 0));
      for (let i = 0; i <= n; i++) trims.push(box(0.08, gh + 0.12, 0.07, x, yc, -gl / 2 + i * seg));
      for (let i = 0; i < n; i++) {
        const z = -gl / 2 + (i + 0.5) * seg;
        if (doorOnFace(sx) && Math.abs(z) < dw / 2 + seg / 2) continue;
        glass.push(box(0.03, gh, seg - 0.07, sx * (w / 2 + 0.005), yc, z));
      }
    }
  }

  // stair tower: tall glazed slits on the short faces without the door, dark landings visible through nothing –
  // the tower stays a solid, darker volume with a roof hatch
  if (kind === "trapp") {
    const sh = wallH - 0.7, sy = BASE_H + 0.35 + sh / 2, sw = 0.5;
    for (const sz of [-1, 1]) {
      if ((doorSide === "south" && sz > 0) || (doorSide === "north" && sz < 0)) continue;
      const z = sz * (l / 2 + 0.02);
      trims.push(box(sw + 0.12, 0.06, 0.08, 0, sy + sh / 2 + 0.03, z), box(sw + 0.12, 0.06, 0.08, 0, sy - sh / 2 - 0.03, z));
      trims.push(box(0.06, sh + 0.12, 0.08, -(sw / 2 + 0.03), sy, z), box(0.06, sh + 0.12, 0.08, sw / 2 + 0.03, sy, z));
      glass.push(box(sw, sh, 0.03, 0, sy, sz * (l / 2 + 0.005)));
    }
    galv.push(box(0.9, 0.12, 0.9, 0, h + 0.06, 0));
  }

  // technical module: louvre panel instead of windows, roof-mounted air handling unit
  if (kind === "teknisk") {
    const lw = Math.min(1.4, l * 0.3), lh = 1.2, ly = BASE_H + 1.6;
    louvre.push(box(0.05, lh, lw, w / 2 + 0.015, ly, 0));
    for (let i = 0; i < 6; i++) galv.push(box(0.03, 0.02, lw - 0.1, w / 2 + 0.045, ly - lh / 2 + 0.1 + i * ((lh - 0.2) / 5), 0));
    trims.push(box(0.06, lh + 0.1, 0.06, w / 2 + 0.02, ly, -(lw / 2 + 0.03)), box(0.06, lh + 0.1, 0.06, w / 2 + 0.02, ly, lw / 2 + 0.03));
    const aw = Math.min(1.2, w * 0.5), al = Math.min(1.6, l * 0.3);
    galv.push(box(aw, 0.6, al, 0, h + 0.3, -l * 0.12), cyl(Math.min(0.28, aw * 0.3), 0.08, 0, h + 0.64, -l * 0.12), box(0.25, 0.25, 0.9, aw / 2 - 0.15, h + 0.13, -l * 0.12 + al / 2 + 0.45));
  }

  // roof hoods (ventilation) on habitable modules; a larger kitchen exhaust on canteens
  if (FLAT_ROOF_VENT.has(kind)) galv.push(cyl(0.1, 0.34, w / 4, h + 0.17, -l / 4), cyl(0.15, 0.04, w / 4, h + 0.36, -l / 4));
  if (kind === "kantine") galv.push(cyl(0.22, 0.5, -w / 4, h + 0.25, l / 4), cyl(0.3, 0.05, -w / 4, h + 0.52, l / 4), cyl(0.1, 0.34, w / 4, h + 0.17, -l / 3), cyl(0.15, 0.04, w / 4, h + 0.36, -l / 3));

  // foundation blocks (only rendered on the ground floor)
  const found = [];
  const rows = Math.max(2, Math.ceil(l / 3) + 1);
  for (let i = 0; i < rows; i++) {
    const z = -l / 2 + 0.35 + (i * (l - 0.7)) / (rows - 1);
    found.push(box(0.4, FOUNDATION_H, 0.4, -(w / 2 - 0.35), -FOUNDATION_H / 2, z), box(0.4, FOUNDATION_H, 0.4, w / 2 - 0.35, -FOUNDATION_H / 2, z));
  }

  const parts = {
    body, base, cap, membrane,
    frame: merge(frameItems),
    trims: merge(trims),
    glass: merge(glass),
    frost: merge(frost),
    galv: merge(galv),
    louvre: merge(louvre),
    foundation: merge(found),
    entrance: merge(atFace(entranceItems(dw), face, doorDx)),
    splash: merge(splash),
    grime: merge(grime),
    bounds: new THREE.BoxGeometry(w + 0.06, h + 0.02, l + 0.06).translate(0, h / 2 + 0.01, 0),
  };
  partCache.set(key, parts);
  return parts;
}

/**
 * Parametric module built from the type's dimensions – the default `ModelAdapter`. A type with `model_url` would be
 * rendered by a GLTF adapter instead (same props, same placement contract).
 *
 * Level-1 realism: corrugated steel cladding (procedural colour/normal/roughness maps in metric UV), anthracite
 * floor frame / corner profiles / roof flashing, grey membrane roof, framed windows with reflective glass and sills,
 * doors with frames and handles, continuous corridor glazing, roof hoods, technical AHU, foundation blocks and an
 * entrance landing with railings where a ground-floor door faces open air. Colour is still reserved for meaning:
 * selection (accent), conflicts (rose) and delivery status tint the cladding.
 */
export const ParametricModule = React.memo(function ParametricModule({ type, floorHeight, color, opacity = 1, selected, hovered, conflict, ghost, floor = 0, entrance = false, lit = false }) {
  const d = dims(type);
  const span = floorSpan(type);
  const h = Math.max(2.2, Math.min(d.h, floorHeight * span) - 0.06);
  const w = d.w - 0.04;
  const l = d.l - 0.04;
  const stories = span > 1 ? span : Math.max(1, Math.round(h / floorHeight));
  const parts = useMemo(() => partsFor(type, w, h, l, stories, floorHeight), [type, w, h, l, stories, floorHeight]);
  const tex = claddingTextures();
  const photo = usePhotoMaps();
  const grimeTex = grimeTextures();
  const body = conflict ? PALETTE.conflict : color;
  const transparent = opacity < 1;
  const emissive = selected ? ACCENT : hovered ? "#ffffff" : "#000000";
  const emissiveIntensity = selected ? 0.06 : hovered ? 0.05 : 0;
  const mat = { transparent, opacity, depthWrite: !transparent };
  const shadow = !transparent;
  const outline = selected || conflict || ghost;
  const edgeColor = conflict ? "#b42318" : ACCENT;

  return (
    <group>
      {/* invisible envelope: single hit target + selection outline host */}
      <mesh geometry={parts.bounds}>
        <meshBasicMaterial visible={false} />
        {outline && <Edges color={edgeColor} threshold={15} lineWidth={selected ? 1.6 : 1.2} transparent opacity={(conflict ? 0.9 : ghost ? 0.85 : 1) * (transparent ? Math.max(0.6, opacity) : 1)} dashed={ghost} dashSize={0.4} gapSize={0.25} />}
      </mesh>
      <mesh geometry={parts.body} castShadow={shadow} receiveShadow={shadow}>
        {photo?.cladding ? (
          <meshStandardMaterial color={body} normalMap={photo.cladding.normalMap} normalScale={[0.9, 0.9]} roughnessMap={photo.cladding.roughnessMap} roughness={1} aoMap={photo.cladding.aoMap} aoMapIntensity={0.7} metalness={0.14} emissive={emissive} emissiveIntensity={emissiveIntensity} {...mat} />
        ) : (
          <meshStandardMaterial color={body} map={tex?.map} normalMap={tex?.normalMap} normalScale={[0.7, 0.7]} roughnessMap={tex?.roughnessMap} roughness={1} metalness={0.12} emissive={emissive} emissiveIntensity={emissiveIntensity} {...mat} />
        )}
      </mesh>
      {!transparent && grimeTex && parts.splash && (
        <mesh geometry={parts.splash}>
          <meshStandardMaterial color="#1b2025" alphaMap={grimeTex.splash} transparent opacity={0.42} depthWrite={false} polygonOffset polygonOffsetFactor={-1} roughness={1} />
        </mesh>
      )}
      {!transparent && grimeTex && parts.grime && (
        <mesh geometry={parts.grime}>
          <meshStandardMaterial color="#1b2025" alphaMap={grimeTex.streak} transparent opacity={0.3} depthWrite={false} polygonOffset polygonOffsetFactor={-1} roughness={1} />
        </mesh>
      )}
      <mesh geometry={parts.base} castShadow={shadow} receiveShadow={shadow}>
        <meshStandardMaterial color={PALETTE.frame} roughness={0.55} metalness={0.35} {...mat} />
      </mesh>
      <mesh geometry={parts.frame} castShadow={shadow}>
        <meshStandardMaterial color={PALETTE.frame} roughness={0.5} metalness={0.4} {...mat} />
      </mesh>
      <mesh geometry={parts.cap} castShadow={shadow} receiveShadow={shadow}>
        <meshStandardMaterial color={PALETTE.trim} roughness={0.5} metalness={0.4} {...mat} />
      </mesh>
      <mesh geometry={parts.membrane} receiveShadow={shadow}>
        <meshStandardMaterial color={shade(body, -0.16)} roughness={0.88} metalness={0.04} emissive={emissive} emissiveIntensity={emissiveIntensity * 0.6} {...mat} />
      </mesh>
      {parts.trims && (
        <mesh geometry={parts.trims} castShadow={shadow}>
          <meshStandardMaterial color={PALETTE.trim} roughness={0.45} metalness={0.45} {...mat} />
        </mesh>
      )}
      {parts.glass && (
        <mesh geometry={parts.glass}>
          {lit ? (
            <meshStandardMaterial color="#5a4a30" emissive="#ffd39a" emissiveIntensity={1.6} roughness={0.2} metalness={0.2} {...mat} />
          ) : (
            <meshStandardMaterial color={PALETTE.glass} roughness={0.1} metalness={0.6} envMapIntensity={1.5} {...mat} />
          )}
        </mesh>
      )}
      {parts.frost && (
        <mesh geometry={parts.frost}>
          {lit ? (
            <meshStandardMaterial color="#6a6050" emissive="#ffe2b8" emissiveIntensity={1.1} roughness={0.5} metalness={0.1} {...mat} />
          ) : (
            <meshStandardMaterial color={PALETTE.glassFrost} roughness={0.45} metalness={0.15} {...mat} />
          )}
        </mesh>
      )}
      {parts.galv && (
        <mesh geometry={parts.galv} castShadow={shadow}>
          <meshStandardMaterial color={PALETTE.galv} roughness={0.38} metalness={0.7} {...mat} />
        </mesh>
      )}
      {parts.louvre && (
        <mesh geometry={parts.louvre}>
          <meshStandardMaterial color={PALETTE.louvre} roughness={0.6} metalness={0.3} {...mat} />
        </mesh>
      )}
      {floor === 0 && !ghost && parts.foundation && (
        <mesh geometry={parts.foundation} castShadow={shadow} receiveShadow={shadow}>
          <meshStandardMaterial color={PALETTE.concrete} roughness={0.95} metalness={0} {...mat} />
        </mesh>
      )}
      {floor === 0 && entrance && !ghost && parts.entrance && (
        <mesh geometry={parts.entrance} castShadow={shadow}>
          <meshStandardMaterial color={PALETTE.galv} roughness={0.38} metalness={0.7} {...mat} />
        </mesh>
      )}
    </group>
  );
});

/** Chooses the renderer for a module type. GLB/IFC adapters plug in here without touching the editor. */
export function ModelAdapter(props) {
  // A future GLTF adapter: if (props.type.model_url) return <GltfModule {...props} />;
  return <ParametricModule {...props} />;
}

/**
 * A placed module: positioning group → animation group (imperative, for staged reveals) → rotated model.
 * Ground-floor modules are lifted onto their foundation blocks; upper floors stack on the module below.
 * `markers` renders connection-point rings on the floor around the module ({dx, dz, active}).
 */
export const ModuleMesh = React.forwardRef(function ModuleMesh({ m, type, floorHeight, color, dimmed, selected, hovered, conflict, ghost = false, entrance = false, night = false, label, compactLabel = false, markers, onPointerDown, onPointerOver, onPointerOut, onDoubleClick }, animRef) {
  const fp = footprint(type, m.rot);
  const y0 = m.floor * floorHeight + FOUNDATION_H;
  const groundY = m.floor === 0 ? -FOUNDATION_H : 0;
  const rotY = THREE.MathUtils.degToRad(-(m.rot || 0));
  const opacity = ghost ? 0.5 : dimmed ? 0.14 : 1;
  const d = dims(type);
  const h = Math.min(d.h, floorHeight * floorSpan(type));
  // slight per-module paint variation (±2 %) so a row of identical modules does not read as copy-paste
  const hash = m.id ? hashId(m.id) : 0;
  const tint = useMemo(() => (ghost || !m.id ? color : shade(color, ((hash % 100) / 100 - 0.5) * 0.045)), [color, m.id, ghost, hash]);
  // at night roughly two of three habitable modules have their lights on
  const lit = night && !ghost && (hash >> 8) % 3 !== 0 && type.kind !== "lager" && type.kind !== "teknisk";
  return (
    <group position={[m.x + fp.sx / 2, y0, m.z + fp.sz / 2]}>
      <group ref={animRef}>
        <group rotation={[0, rotY, 0]} onPointerDown={onPointerDown} onPointerOver={onPointerOver} onPointerOut={onPointerOut} onDoubleClick={onDoubleClick}>
          <ModelAdapter type={type} floorHeight={floorHeight} color={tint} opacity={opacity} selected={selected} hovered={hovered && !dimmed} conflict={conflict} ghost={ghost} floor={m.floor} entrance={entrance} lit={lit} />
        </group>
        {selected && !dimmed && (
          <mesh position={[0, groundY + 0.012, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[fp.sx + 0.6, fp.sz + 0.6]} />
            <meshBasicMaterial color={ACCENT} transparent opacity={0.09} depthWrite={false} toneMapped={false} />
          </mesh>
        )}
        {markers && !dimmed && markers.map((k, i) => (
          <mesh key={i} position={[k.dx, groundY + 0.03, k.dz]} rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[0.14, 0.24, 24]} />
            <meshBasicMaterial color={k.active ? ACCENT : "#8a94a3"} transparent opacity={k.active ? 0.95 : 0.55} depthWrite={false} toneMapped={false} />
          </mesh>
        ))}
        {label && !dimmed && (
          <Html position={[0, h + 0.45, 0]} center zIndexRange={[20, 0]} style={{ pointerEvents: "none" }}>
            {compactLabel ? (
              <div className="whitespace-nowrap rounded-[3px] bg-background/85 px-1 py-px font-mono text-[10px] leading-4 text-muted-foreground">{label.title}</div>
            ) : (
              <div className={"whitespace-nowrap rounded-md bg-background/95 px-2 py-1 leading-4 shadow-pop " + (selected ? "ring-1 ring-primary/40" : "")}>
                <div className="font-mono text-[11px] font-medium text-foreground">{label.title}</div>
                {label.sub && <div className="text-[10.5px] text-muted-foreground">{label.sub}</div>}
              </div>
            )}
          </Html>
        )}
      </group>
    </group>
  );
});
